import type { ChordAccompaniment, DrumPattern, Pattern } from '../types';
import { clonePattern } from './pattern';
import { createEmptyDrumPattern, resizeDrumPattern } from './drumPattern';
import { resizeChordAccompaniment } from './chordAccompaniment';
import { cloneVoicings } from './chordVoicing';

const STORAGE_KEY = 'boom-melody-library';

export interface SavedMelodyPattern {
  id: string;
  title: string;
  description: string;
  bpm: number;
  pattern: Pattern;
  drumPattern: DrumPattern | null;
  chordAccompaniment: ChordAccompaniment | null;
  custom: true;
  createdAt: number;
}

function cloneDrumPattern(drum: DrumPattern | null): DrumPattern | null {
  if (!drum) return null;
  const base = resizeDrumPattern(createEmptyDrumPattern(drum.measures), drum.measures);
  return {
    ...base,
    enabled: drum.enabled,
    steps: {
      kick: [...drum.steps.kick],
      snare: [...drum.steps.snare],
      hihat: [...drum.steps.hihat],
      shaker: [...drum.steps.shaker],
    },
  };
}

function cloneChordAccompaniment(chords: ChordAccompaniment | null): ChordAccompaniment | null {
  if (!chords) return null;
  return resizeChordAccompaniment(
    {
      enabled: chords.enabled,
      voicings: cloneVoicings(chords.voicings),
      measures: chords.measures.map((m) => ({
        steps: Object.fromEntries(
          Object.entries(m.steps).map(([k, v]) => [k, [...v]]),
        ) as ChordAccompaniment['measures'][0]['steps'],
      })),
    },
    chords.measures.length,
  );
}

function loadRaw(): SavedMelodyPattern[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedMelodyPattern[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persist(list: SavedMelodyPattern[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function loadMelodyLibrary(): SavedMelodyPattern[] {
  return loadRaw().sort((a, b) => b.createdAt - a.createdAt);
}

export interface SaveMelodyInput {
  id?: string;
  title: string;
  description?: string;
  bpm: number;
  pattern: Pattern;
  drumPattern?: DrumPattern | null;
  chordAccompaniment?: ChordAccompaniment | null;
}

export function saveMelodyPattern(input: SaveMelodyInput): SavedMelodyPattern {
  const list = loadRaw();
  const id = input.id ?? `melody-${Date.now()}`;
  const existing = list.findIndex((entry) => entry.id === id);
  const entry: SavedMelodyPattern = {
    id,
    title: input.title.trim() || 'Mi patrón',
    description: input.description?.trim() || 'Patrón de boomwhackers',
    bpm: input.bpm,
    pattern: clonePattern(input.pattern),
    drumPattern: cloneDrumPattern(input.drumPattern ?? null),
    chordAccompaniment: cloneChordAccompaniment(input.chordAccompaniment ?? null),
    custom: true,
    createdAt: existing >= 0 ? list[existing].createdAt : Date.now(),
  };

  if (existing >= 0) {
    list[existing] = entry;
  } else {
    list.unshift(entry);
  }

  persist(list);
  return entry;
}

export function deleteMelodyPattern(id: string): void {
  persist(loadRaw().filter((entry) => entry.id !== id));
}

export function getMelodyPattern(id: string): SavedMelodyPattern | null {
  return loadRaw().find((entry) => entry.id === id) ?? null;
}
